import React, { useState, useEffect, useRef } from "react";
import ReactQuill from "react-quill";
import { v4 as uuidv4 } from "uuid";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { storage } from "../../../../db/firebase";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../../context/AuthContext";
import "react-quill/dist/quill.snow.css";

interface CommentInputProps {
  onAddComment: (comment: any) => void | Promise<void>;
  maxFileSizeMB?: number;
}

function isQuillEmpty(html: string) {
  const text = html
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/\s+/g, " ")
    .trim();
  return text.length === 0;
}

const CommentInput: React.FC<CommentInputProps> = ({
  onAddComment,
  maxFileSizeMB = 25,
}) => {
  const { t } = useTranslation();
  const { profile, user } = useAuth();
  const [newComment, setNewComment] = useState("");
  const [attachment, setAttachment] = useState<File | null>(null);
  const [attachmentError, setAttachmentError] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const userName =
    [profile?.firstName, profile?.lastName].filter(Boolean).join(" ").trim() ||
    user?.email ||
    t("anonymousUser");

  useEffect(() => {
    setError("");
  }, [newComment, attachment]);

  const handleAddComment = async () => {
    if (saving) return;
    if (isQuillEmpty(newComment) && !attachment) {
      setError(t("noteEmptyError") || "Write a note or attach a file.");
      return;
    }

    setSaving(true);
    setError("");
    setAttachmentError("");

    try {
      let attachmentUrl = "";
      let attachmentType = "";
      let attachmentName = "";
      let isAudio = false;

      if (attachment) {
        if (attachment.size / (1024 * 1024) > maxFileSizeMB) {
          setAttachmentError(t("fileSizeError", { maxFileSizeMB }));
          setSaving(false);
          return;
        }

        const storageRef = ref(storage, `attachments/${uuidv4()}`);
        await uploadBytes(storageRef, attachment);
        attachmentUrl = await getDownloadURL(storageRef);
        attachmentType = attachment.type.split("/")[0];
        attachmentName = attachment.name;
        isAudio = attachmentType === "audio";
      }

      await onAddComment({
        id: uuidv4(),
        userName,
        userId: user?.uid || "",
        comment: isQuillEmpty(newComment) ? "" : newComment,
        timestamp: new Date().toISOString(),
        attachmentUrl,
        attachmentType,
        attachmentName,
        isAudio,
      });

      setNewComment("");
      setAttachment(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
    } catch (err) {
      console.error("Failed to add note:", err);
      setError(t("noteSaveFailed") || "Could not save note. Try again.");
    } finally {
      setSaving(false);
    }
  };

  const modules = {
    toolbar: [
      ["bold", "italic", "underline"],
      [{ list: "ordered" }, { list: "bullet" }],
      ["link"],
      ["clean"],
    ],
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="note-quill rounded-xl border border-gray-200 dark:border-gray-600 overflow-hidden bg-white dark:bg-gray-800">
        <ReactQuill
          theme="snow"
          value={newComment}
          onChange={setNewComment}
          modules={modules}
          placeholder={t("newNotesPlaceholder")}
          className="note-quill-editor"
        />
      </div>

      <div className="flex flex-col sm:flex-row gap-2">
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={saving}
          className="w-full sm:w-auto px-4 py-3 sm:py-2.5 rounded-xl font-medium border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-60"
        >
          {t("chooseFileButton")}
        </button>
        <button
          type="button"
          onClick={handleAddComment}
          disabled={saving}
          className="w-full sm:flex-1 py-3 sm:py-2.5 rounded-xl font-medium bg-[#5932EA] text-white hover:bg-[#4a28c7] transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
        >
          {saving ? (
            <>
              <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              {t("saving")}
            </>
          ) : (
            t("addNotesButton")
          )}
        </button>
      </div>

      <input
        type="file"
        ref={fileInputRef}
        accept="image/*, audio/*, application/pdf, application/msword, .docx, .ppt, .pptx"
        onChange={(e) => setAttachment(e.target.files?.[0] || null)}
        className="hidden"
      />

      {attachment && (
        <p className="text-sm text-gray-600 dark:text-gray-400">
          {t("selectedFileLabel", { fileName: attachment.name })}
          <button
            type="button"
            onClick={() => {
              setAttachment(null);
              if (fileInputRef.current) fileInputRef.current.value = "";
            }}
            className="ml-2 text-[#5932EA] hover:underline"
          >
            {t("remove")}
          </button>
        </p>
      )}
      {attachmentError && (
        <p className="text-sm text-red-500 dark:text-red-400">{attachmentError}</p>
      )}
      {error && <p className="text-sm text-red-500 dark:text-red-400">{error}</p>}
    </div>
  );
};

export default CommentInput;
